// BIOLITH MASONRY — the nine patterns of the biolith kubes (biolith.mjs; the owner's proposal, confirmed Oct 13).
//
// Each pattern is ONE grey canvas, shared by the biolith of every pigment and by its bit: TextureEngine sets the
// material's colour to the kube's own hex (the regolith palette's, exactly) and the GPU multiplies this canvas by it, so
// the pigment is the colour and the pattern is the light and shade on it. So the shared-slot rules hold here as for
// the crust (texture-crust.js) and the glass: drawn in greys over white, from a FIXED seed, with no colour argument and
// no reading of anybody's hex (every maker takes no parameters), the same canvas in every session.
//
// THE OWNER'S PICKS (Oct 13): JOINTS "TIGHT" — fine and subtle, so a pattern reads as a texture on the colour rather
// than a grid over it: joints at about 0.6 grey and 0.45 of the proposal's widths (9 texels for bricks on this 1024
// canvas, 2.25 on the page's 256). EDGES "FLOW" — a pattern runs on across kubes, unbroken: every brick, stone and
// cloud that crosses an edge of the canvas is drawn again where it comes back in on the far side, and the courses,
// lattices and plates are laid out on the canvas's period, so a wall of these kubes is one wall. The CARVED KUBE is the
// exception by design: one motif, framed, on each face.
//
// BRIGHTNESS. The bricks sit at 0.92 grey and the joints at 0.62, so a canvas averages about 0.90, the mean the crust is
// held to (texture-crust.js: 0.90 Mineral, 0.93 Regolith): a biolith of Solis reads as the same colour as the Solis
// Regolith it is made from, with the pattern as its shading. No pattern is levelled by a pixel pass (no maker does one:
// tests/material-looks.test.mjs); the levels are these constants, measured in Chrome (tests/biolith.test.mjs pins them).
//
// FLAGSTONE is POLYGONS, the cells of a Voronoi tiling of the torus cut by half-planes (as texture-crust.js's rock
// plates are), filled and stroked: the proposal page's per-pixel field was the page's shortcut only.
//
// THREE-free and DOM-light (it asks the document for a canvas and nothing else), so a test can record what it draws.

export const BIOLITH_TEX_SIZE = 1024;
const S = BIOLITH_TEX_SIZE;
export const BIOLITH_BLOCK = 0.92, BIOLITH_JOINT = 0.62;   // the bricks' grey and the joints' (the owner's "tight")
export const BIOLITH_GAP = 9;                               // a brick joint, in texels (5 × 0.45 on the page's 256, × 4)

function rng(seed) { let x = seed >>> 0; return () => ((x = (Math.imul(x, 1664525) + 1013904223) >>> 0) / 4294967296); }
const grey = (v) => { const c = Math.max(0, Math.min(255, Math.round(v * 255))); return `rgb(${c}, ${c}, ${c})`; };
function canvasOf(fill) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = grey(fill);
  ctx.fillRect(0, 0, S, S);
  return { canvas, ctx };
}
// The offsets a shape whose box is [x0, x1] × [y0, y1] must be drawn at so the canvas tiles: itself, and once more for
// every edge it crosses (a corner's three copies when it crosses two).
function offsets(x0, y0, x1, y1) {
  const out = [];
  for (const dx of [-S, 0, S]) for (const dy of [-S, 0, S])
    if (x1 + dx > 0 && x0 + dx < S && y1 + dy > 0 && y0 + dy < S) out.push([dx, dy]);
  return out;
}
const mod = (v) => ((v % S) + S) % S;

// BLOCKS: rectangles laid on the canvas's period, each its own grey, the joint showing between them. `rects` are
// [x, y, w, h] in one period (x, y may lie anywhere: they are taken modulo the canvas).
function blocks(ctx, rects, R, gap) {
  const g = gap / 2, bevel = 6;
  for (const [rx, ry, w, h] of rects) {
    const x = mod(rx), y = mod(ry), v = BIOLITH_BLOCK + (R() - 0.5) * 0.05;
    for (const [dx, dy] of offsets(x, y, x + w, y + h)) {
      const X = x + dx + g, Y = y + dy + g, W = w - gap, H = h - gap;
      ctx.fillStyle = grey(v); ctx.fillRect(X, Y, W, H);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.12)'; ctx.fillRect(X, Y, W, bevel);              // a lit top edge…
      ctx.fillStyle = 'rgba(0, 0, 0, 0.06)'; ctx.fillRect(X, Y + H - bevel, W, bevel);        // …and a shaded foot
    }
  }
}
// SPECKS: the grain of the stone, tiny squares of a little black or white, wrapped like everything else.
function specks(ctx, R, n, a) {
  for (let i = 0; i < n; i++) {
    const dark = R() < 0.5, r = 2.4 + R() * 5.6, x = R() * S, y = R() * S;
    ctx.fillStyle = dark ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
    for (const [dx, dy] of offsets(x, y, x + r, y + r)) ctx.fillRect(x + dx, y + dy, r, r);
  }
}

// RUNNING BOND ("Bricks"): four courses a kube, two bricks a course, each course offset by half a brick.
export function drawBiolithRunning() {
  const { canvas, ctx } = canvasOf(BIOLITH_JOINT), R = rng(0xb10001), r = [];
  for (let row = 0; row < 4; row++) for (let i = 0; i < 2; i++) r.push([i * 512 + (row % 2) * 256, row * 256, 512, 256]);
  blocks(ctx, r, R, BIOLITH_GAP); specks(ctx, R, 900, 0.05);
  return canvas;
}
// STACK BOND ("Stacked Bricks"): the same bricks straight above each other, the joints in a grid.
export function drawBiolithStack() {
  const { canvas, ctx } = canvasOf(BIOLITH_JOINT), R = rng(0xb10002), r = [];
  for (let row = 0; row < 4; row++) for (let i = 0; i < 2; i++) r.push([i * 512, row * 256, 512, 256]);
  blocks(ctx, r, R, BIOLITH_GAP); specks(ctx, R, 900, 0.05);
  return canvas;
}
// HERRINGBONE: bricks of 2 × 1 units in a zigzag of right angles, on a lattice ((u, u) and (−2u, 2u)) whose periods
// are the canvas's own, so the zigzag runs on across every edge. Sixteen lattice points a period, two bricks each.
export function drawBiolithHerring() {
  const { canvas, ctx } = canvasOf(BIOLITH_JOINT), R = rng(0xb10003), u = 128, seen = new Set(), r = [];
  for (let a = -16; a <= 16; a++) for (let b = -16; b <= 16; b++) {
    const x = mod((a - 2 * b) * u), y = mod((a + 2 * b) * u), k = x + ',' + y;
    if (seen.has(k)) continue;
    seen.add(k); r.push([x, y, 2 * u, u], [x + 2 * u, y - u, u, 2 * u]);
  }
  blocks(ctx, r, R, BIOLITH_GAP * 0.8); specks(ctx, R, 900, 0.05);
  return canvas;
}
// BASKETWEAVE: sixteen squares, each a pair of bricks, turned a quarter from its neighbours.
export function drawBiolithBasket() {
  const { canvas, ctx } = canvasOf(BIOLITH_JOINT), R = rng(0xb10004), s = 256, r = [];
  for (let i = 0; i < 4; i++) for (let k = 0; k < 4; k++) {
    const x = i * s, y = k * s;
    if ((i + k) % 2) r.push([x, y, s, s / 2], [x, y + s / 2, s, s / 2]);
    else r.push([x, y, s / 2, s], [x + s / 2, y, s / 2, s]);
  }
  blocks(ctx, r, R, BIOLITH_GAP * 0.8); specks(ctx, R, 900, 0.05);
  return canvas;
}
// SQUARE TILES: four by four, with the finest joints of all.
export function drawBiolithTile() {
  const { canvas, ctx } = canvasOf(BIOLITH_JOINT), R = rng(0xb10006), r = [];
  for (let i = 0; i < 4; i++) for (let k = 0; k < 4; k++) r.push([i * 256, k * 256, 256, 256]);
  blocks(ctx, r, R, BIOLITH_GAP * 0.8); specks(ctx, R, 700, 0.04);
  return canvas;
}

// FLAGSTONE: sixteen irregular stones, the Voronoi cells of a jittered 4 × 4 grid on the torus. Each cell is cut out of a
// square by the half-plane nearer its own seed than each wrapped copy of every other seed (texture-crust.js plateCells'
// way), so the cells meet exactly and tile; each is filled in its own grey, then every edge stroked as a fine joint.
export const FLAG_STONES = 16;
function flagCells(R) {
  const n = 4, step = S / n, seeds = [];
  for (let i = 0; i < n; i++) for (let k = 0; k < n; k++) seeds.push([(i + 0.5 + (R() - 0.5) * 0.9) * step, (k + 0.5 + (R() - 0.5) * 0.9) * step, R()]);
  const copies = [];
  for (const [x, y] of seeds) for (const dx of [-S, 0, S]) for (const dy of [-S, 0, S]) copies.push([x + dx, y + dy]);
  return seeds.map(([sx, sy, shade]) => {
    let poly = [[sx - S, sy - S], [sx + S, sy - S], [sx + S, sy + S], [sx - S, sy + S]];
    for (const [ox, oy] of copies) {
      if (Math.abs(ox - sx) < 1e-9 && Math.abs(oy - sy) < 1e-9) continue;
      const nx = ox - sx, ny = oy - sy, c = (ox * ox + oy * oy - sx * sx - sy * sy) / 2, out = [];
      for (let i = 0; i < poly.length; i++) {
        const p = poly[i], q = poly[(i + 1) % poly.length], fp = nx * p[0] + ny * p[1] - c, fq = nx * q[0] + ny * q[1] - c;
        if (fp <= 0) out.push(p);
        if ((fp < 0) !== (fq < 0) && fp !== fq) { const t = fp / (fp - fq); out.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]); }
      }
      poly = out; if (!poly.length) break;
    }
    return { poly, shade };
  });
}
export function drawBiolithFlag() {
  const { canvas, ctx } = canvasOf(BIOLITH_BLOCK), R = rng(0xb10005), cells = flagCells(R);
  const box = (poly) => { let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity; for (const [x, y] of poly) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; } return [x0, y0, x1, y1]; };
  const trace = (poly, dx, dy) => { ctx.beginPath(); poly.forEach(([x, y], i) => (i ? ctx.lineTo(x + dx, y + dy) : ctx.moveTo(x + dx, y + dy))); ctx.closePath(); };
  for (const pass of ['fill', 'joint']) for (const { poly, shade } of cells) {
    const [x0, y0, x1, y1] = box(poly), pad = BIOLITH_GAP;
    for (const [dx, dy] of offsets(x0 - pad, y0 - pad, x1 + pad, y1 + pad)) {
      trace(poly, dx, dy);
      if (pass === 'fill') { ctx.fillStyle = grey(BIOLITH_BLOCK - 0.03 + shade * 0.06); ctx.fill(); }
      else { ctx.strokeStyle = grey(BIOLITH_JOINT); ctx.lineWidth = BIOLITH_GAP; ctx.lineJoin = 'round'; ctx.stroke(); }
    }
  }
  specks(ctx, R, 800, 0.05);
  return canvas;
}

// POLISHED: one smooth face, soft clouding in it — for when the colour is the point.
export function drawBiolithSmooth() {
  const { canvas, ctx } = canvasOf(BIOLITH_BLOCK), R = rng(0xb10007);
  for (let i = 0; i < 26; i++) {
    const x = R() * S, y = R() * S, r = 80 + R() * 200, dark = R() < 0.5;
    for (const [dx, dy] of offsets(x - r, y - r, x + r, y + r)) {
      const X = x + dx, Y = y + dy, g = ctx.createRadialGradient(X, Y, 0, X, Y, r);
      // Each cloud fades to ITS OWN colour at no alpha, so a pale cloud never greys on its way out (a canvas may blend
      // the stops unpremultiplied).
      g.addColorStop(0, dark ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.06)');
      g.addColorStop(1, dark ? 'rgba(0, 0, 0, 0)' : 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = g; ctx.fillRect(X - r, Y - r, 2 * r, 2 * r);
    }
  }
  return canvas;
}
// FLUTED: eight upright grooves, lit on one side. Eight flutes divide the canvas exactly, so they run on across kubes.
export function drawBiolithFluted() {
  const { canvas, ctx } = canvasOf(BIOLITH_BLOCK), R = rng(0xb10008), w = S / 8;
  for (let i = 0; i < 8; i++) {
    const x = i * w, g = ctx.createLinearGradient(x, 0, x + w, 0);
    g.addColorStop(0, grey(0.74)); g.addColorStop(0.3, grey(0.98)); g.addColorStop(0.75, grey(0.91)); g.addColorStop(1, grey(0.7));
    ctx.fillStyle = g; ctx.fillRect(x, 0, w, S);
  }
  specks(ctx, R, 600, 0.04);
  return canvas;
}
// CARVED KUBE: a framed panel with the kube carved in it, a hexagon and its three edges (Kubicor's mark): ONE ON EACH
// FACE, the one pattern that does not run on across kubes (the owner's choice, by design).
export function drawBiolithGlyph() {
  const { canvas, ctx } = canvasOf(BIOLITH_BLOCK), R = rng(0xb10009), c = S / 2, r = 280;
  ctx.lineWidth = 22; ctx.strokeStyle = grey(0.7); ctx.strokeRect(64, 64, S - 128, S - 128);
  ctx.lineWidth = 8; ctx.strokeStyle = grey(0.99); ctx.strokeRect(88, 88, S - 176, S - 176);
  ctx.beginPath();
  for (let i = 0; i < 6; i++) { const a = Math.PI / 6 + i * Math.PI / 3; ctx[i ? 'lineTo' : 'moveTo'](c + r * Math.cos(a), c + r * Math.sin(a)); }
  ctx.closePath(); ctx.lineWidth = 24; ctx.lineJoin = 'round'; ctx.strokeStyle = grey(0.66); ctx.stroke();
  ctx.beginPath();
  for (const a of [Math.PI / 2, Math.PI * 7 / 6, Math.PI * 11 / 6]) { ctx.moveTo(c, c); ctx.lineTo(c + r * Math.cos(a), c + r * Math.sin(a)); }
  ctx.lineCap = 'round'; ctx.stroke();
  specks(ctx, R, 800, 0.05);
  return canvas;
}

// The makers by pattern id (biolith.mjs BIOLITH_PATTERNS), for TextureEngine's 'biolith:<id>' slots.
export const BIOLITH_MAKERS = Object.freeze({
  running: drawBiolithRunning, stack: drawBiolithStack, herring: drawBiolithHerring, basket: drawBiolithBasket, flag: drawBiolithFlag,
  tile: drawBiolithTile, smooth: drawBiolithSmooth, fluted: drawBiolithFluted, glyph: drawBiolithGlyph,
});
